import { Link } from 'react-router';
import { assetUrl } from '../lib/api';
import type { PublicEvent } from '../lib/queries';
import { initials, useSession } from '../lib/session';

/**
 * The header the brand kit describes: the lockup at 152px, a hairline rule under it, and the
 * avatar as the only other thing in it — tapping it is the way to the profile, which is why no
 * tile advertises one.
 *
 * The logo an organizer uploaded wins (CNT-04), and the PNG in `public/` is what is drawn until
 * they do. That fallback is not temporary scaffolding: it is the client's own mark, it is bundled
 * with the app, and it renders before the event query has resolved — which is what keeps the first
 * paint from being a blank space where the logo goes.
 *
 * The client still owes us a single-line wordmark either way: 400 × 275 is a tall shape for a
 * phone header.
 */
export function TopBar({ event }: { event: PublicEvent | undefined }) {
  const { session } = useSession();
  const logo = assetUrl(event?.branding.logoAssetId);

  return (
    <header className="px-gutter border-b border-border-2 bg-surface pt-5 pb-4">
      <div className="flex items-start justify-between gap-3">
        <Link to="/" aria-label="Home" className="min-w-0">
          <img
            src={logo ?? '/logo-lockup.png'}
            alt={event?.name ?? 'Go For The Greens'}
            className="h-auto max-h-20 w-[152px] max-w-full object-contain object-left"
            // Only stated for the bundled lockup: an uploaded logo is whatever shape the
            // organizer sent, and asserting these dimensions for it would squash it into the
            // wrong one.
            width={logo ? undefined : 400}
            height={logo ? undefined : 275}
          />
        </Link>

        {session && (
          <Link
            to="/account"
            aria-label={`Account: ${session.firstName} ${session.lastName}`}
            className="text-chip mt-1 flex size-tap shrink-0 items-center justify-center rounded-full bg-sand-3 font-semibold text-ink-2"
          >
            {initials(session)}
          </Link>
        )}
      </div>
    </header>
  );
}
